import configJSON from 'config.json';
import Cookies from 'js-cookie';
import { Provider } from '@zoocasa/go-search';
import { getUserFromRequest, isUserAuthenticatedFromRequest, User } from 'contexts';
import {
  ACCEPTED_TERMS_COOKIE_NAME,
  ACCEPTED_TERMS_EXPIRE_DAYS,
  ZOOCASA_TERMS_READ_COOKIE,
} from 'constants/cookies';
import {
  getObjectFromRequestCookie,
  getStringFromRequestCookie,
} from 'utils/cookies';
import { IncomingMessage } from 'http';
import { ComplianceRequirement } from 'types/compliance';

const VOW_PROVIDERS = [
  Provider.PROVIDER_TREB_VOW,
  Provider.PROVIDER_OREB_VOW,
  Provider.PROVIDER_ITSO_VOW,
  Provider.PROVIDER_PILLAR9_VOW,
  Provider.PROVIDER_REBGV_VOW,
  Provider.PROVIDER_VICTORIA_AND_VANCOUVER_ISLAND_BOARD_VOW,
  Provider.PROVIDER_NSAR_VOW,
];

const TERMS_NOT_REQUIRED_PROVIDERS = [
  Provider.PROVIDER_TREB_VOW,
  Provider.PROVIDER_OREB_VOW,
  Provider.PROVIDER_VICTORIA_AND_VANCOUVER_ISLAND_BOARD_VOW,
  Provider.PROVIDER_NSAR_VOW,
];

const BROWSER_BASED_TERMS = [ // These terms will not associate with a user
  Provider.PROVIDER_CREA_DDF,
];

const BROWSER_BASED_TERMS_USER_ID = 'browser_based_terms';

/** Get the provider logo URL for the given provider. */
function getProviderLogoUrl(provider: Provider) {
  return configJSON.compliance.provider.logoUrlTemplate.replace('%PROVIDER_ID%', provider.toString());
}

function isProviderPrivate(provider: Provider) {
  return VOW_PROVIDERS.includes(provider);
}

function isVOWProvider(provider: Provider) {
  return VOW_PROVIDERS.includes(provider);
}

type AcceptedTerms = {
  userId: string;
  acceptedTerms: number[];
};

type AcceptedTermsByUser = {
  userBasedTerms: Provider[];
  browserBasedTerms: Provider[];
};

/**
 * Retrieves the list of accepted terms by user from the browser cookies.
 *
 * @returns An array of accepted terms by user, or an empty array if none are found
 *
 * @example
 * ```typescript
 * // In a component
 * export const MyComponent = () => {
 *   const acceptedTerms = getAcceptedTermsFromCookies();
 *
 *   // Check if user has accepted terms for a specific provider
 *   const hasAcceptedTREB = acceptedTerms.some(term =>
 *     term.userId === userId &&
 *     term.acceptedTerms.includes(Provider.PROVIDER_TREB_VOW)
 *   );
 *
 *   return (
 *     <div>
 *       {hasAcceptedTREB ? 'User has accepted Terms and Conditions' : 'User has not accepted Terms and Conditions'}
 *     </div>
 *   );
 * };
 * ```
 */
const getAcceptedTermsFromCookies = () => {
  const cookieValue = Cookies.get(ACCEPTED_TERMS_COOKIE_NAME);
  return cookieValue ? JSON.parse(cookieValue) as AcceptedTerms[] : [];
};

/**
 * Retrieves the list of accepted terms by user from an incoming HTTP request.
 *
 * @param request The incoming HTTP request object
 * @returns An array of accepted terms by user, or an empty array if none are found
 *
 * @example
 * ```typescript
 * // In getServerSideProps or API route
 * export const getServerSideProps = async (context) => {
 *   const acceptedTerms = getAcceptedTermsCookieFromRequest(context.req);
 *
 *   // Check if user has accepted terms for a specific provider
 *   const hasAcceptedTREB = acceptedTerms.some(term =>
 *     term.userId === userId &&
 *     term.acceptedTerms.includes(Provider.PROVIDER_TREB_VOW)
 *   );
 *
 *   return { props: { hasAcceptedTREB } };
 * };
 * ```
 */
function getAcceptedTermsFromRequest(request: IncomingMessage): AcceptedTerms[] {
  const cookieValue = getObjectFromRequestCookie<AcceptedTerms[]>(ACCEPTED_TERMS_COOKIE_NAME, request);
  return cookieValue || [];
}

function setAcceptedTermsCookie(value: AcceptedTerms[]) {
  Cookies.set(ACCEPTED_TERMS_COOKIE_NAME, JSON.stringify(value), { expires: ACCEPTED_TERMS_EXPIRE_DAYS });
}

function acceptProviderTermsForUser(providerId: Provider, userId?: User['id']) {
  let userIdToSave = userId;
  if (BROWSER_BASED_TERMS.includes(providerId)) { // Put all providers that don't need to associate with a user as BROWSER_BASED_TERMS_USER_ID
    userIdToSave = BROWSER_BASED_TERMS_USER_ID;
  }
  if (userIdToSave) {
    const acceptedTerms = getAcceptedTermsFromCookies();
    const userIndex = acceptedTerms.findIndex(term => term.userId === String(userIdToSave));
    if (userIndex !== -1) { // Existing user: add new providerId only
      // Check if providerId already exists to prevent duplicates
      if (!acceptedTerms[userIndex].acceptedTerms.includes(providerId)) {
        acceptedTerms[userIndex].acceptedTerms.push(providerId);
        setAcceptedTermsCookie(acceptedTerms);
      }
    } else { // New user: add new userId with new providerId
      acceptedTerms.push({ userId: String(userIdToSave), acceptedTerms: [providerId]});
      setAcceptedTermsCookie(acceptedTerms);
    }
  }
}

/**
 * Retrieves all accepted terms for a specific user from an incoming HTTP request.
 *
 * This function extracts and categorizes accepted terms of use cookies from a server-side
 * HTTP request, separating them into user-specific terms and browser-based terms. It's
 * designed to work in conjunction with the client-side cookie management system to
 * determine user access to various real estate data providers.
 *
 * The function categorizes terms into two types:
 * - **User-based terms**: Terms accepted by a specific authenticated user
 * - **Browser-based terms**: Terms accepted anonymously (not tied to a specific user)
 *
 * @param request The incoming HTTP request object containing cookies
 * @param userId Optional user identifier to filter user-specific terms. If not provided,
 *                only browser-based terms will be returned
 * @returns An object containing arrays of accepted terms separated by type
 *
 * @example
 * ```typescript
 * // In getServerSideProps or API route
 * export const getServerSideProps = async (context) => {
 *   const userId = getUserFromRequest(context.req)?.id;
 *   const acceptedTerms = getAllAcceptedTermsByUserFromRequest(context.req, userId);
 *
 *   // Check if user has accepted VOW terms
 *   const hasAcceptedVOWTerms = acceptedTerms.userBasedTerms.some(providerId =>
 *     VOW_PROVIDERS.includes(providerId)
 *   );
 *
 *   // Check if browser has accepted browser-based terms
 *   const hasAcceptedBrowserTerms = acceptedTerms.browserBasedTerms.some(providerId =>
 *     BROWSER_BASED_TERMS.includes(providerId)
 *   );
 *
 *   return {
 *     props: {
 *       hasAcceptedVOWTerms,
 *       hasAcceptedBrowserTerms,
 *       userTerms: acceptedTerms.userBasedTerms,
 *       browserTerms: acceptedTerms.browserBasedTerms
 *     }
 *   };
 * };
 * ```
 */
function getAcceptedTermsByUserFromRequest(request: IncomingMessage, userId?: string | number): AcceptedTermsByUser {
  const acceptedTerms: AcceptedTerms[] = getAcceptedTermsFromRequest(request);
  const userAcceptedTerms: AcceptedTermsByUser = {
    userBasedTerms: [],
    browserBasedTerms: [],
  };
  acceptedTerms.forEach(term => {
    if (term.userId === BROWSER_BASED_TERMS_USER_ID) {
      userAcceptedTerms.browserBasedTerms = term.acceptedTerms;
    } else if (term?.userId === String(userId)) {
      userAcceptedTerms.userBasedTerms = term.acceptedTerms;
    }
  });

  return userAcceptedTerms;
}

function getAcceptedTermsByUserFromCookies(userId?: string | number): AcceptedTermsByUser {
  let browserBasedTerms:Provider[] = [];
  let userBasedTerms:Provider[] = [];
  const browserBasedTermsExists = getAcceptedTermsFromCookies().find((user: { userId: string }) => user.userId === BROWSER_BASED_TERMS_USER_ID);
  if (browserBasedTermsExists) {
    browserBasedTerms = browserBasedTermsExists.acceptedTerms;
  }
  if (userId) {
    const userExists = getAcceptedTermsFromCookies().find(term => term.userId === String(userId));
    if (userExists) {
      userBasedTerms = userExists.acceptedTerms;
    }
  }
  return {
    userBasedTerms,
    browserBasedTerms,
  };
}

function isProviderTermsAccepted(providerId: Provider, userId?: User['id'] | null): boolean {
  if (BROWSER_BASED_TERMS.includes(providerId)) {
    const acceptedTerms = getAcceptedTermsByUserFromCookies(BROWSER_BASED_TERMS_USER_ID);
    return acceptedTerms.browserBasedTerms.includes(providerId);
  } else if (userId) {
    const acceptedTerms = getAcceptedTermsByUserFromCookies(userId);
    return acceptedTerms.userBasedTerms.includes(providerId);
  }
  return false;
}

function isProviderTermsAcceptedFromRequest(request: IncomingMessage, providerId: Provider, userId?: User['id'] | null): boolean {
  if (BROWSER_BASED_TERMS.includes(providerId)) {
    const acceptedTerms = getAcceptedTermsByUserFromRequest(request, BROWSER_BASED_TERMS_USER_ID);
    return acceptedTerms.browserBasedTerms.includes(providerId);
  } else if (userId) {
    const acceptedTerms = getAcceptedTermsByUserFromRequest(request, userId);
    return acceptedTerms.userBasedTerms.includes(providerId);
  }
  return false;
}

function getComplianceRequirementFromRequest(request: IncomingMessage, providerId: number): ComplianceRequirement {
  const isAuthenticated = isUserAuthenticatedFromRequest(request);
  const isPrivate = isProviderPrivate(providerId) && !isAuthenticated;
  const user = getUserFromRequest(request);
  const userId = user?.id;

  if (isPrivate) {
    return ComplianceRequirement.SIGN_IN;
  } else if (TERMS_NOT_REQUIRED_PROVIDERS.includes(providerId)) {
    return ComplianceRequirement.NONE;
  } else if (BROWSER_BASED_TERMS.includes(providerId)) {
    const accepted = isProviderTermsAcceptedFromRequest(request, providerId, userId);
    return accepted ? ComplianceRequirement.NONE : ComplianceRequirement.TERMS_OF_USE;
  } else if (VOW_PROVIDERS.includes(providerId)) {
    const accepted = isProviderTermsAcceptedFromRequest(request, providerId, userId);
    return accepted ? ComplianceRequirement.NONE : ComplianceRequirement.TERMS_OF_USE;
  }
  return ComplianceRequirement.NONE;
}

function getComplianceRequirementFromCookies(providerId: number, userId?: User['id'] | null): ComplianceRequirement {
  const isAuthenticated = userId !== undefined && userId !== null;
  const isPrivate = isProviderPrivate(providerId) && !isAuthenticated;

  if (isPrivate) {
    return ComplianceRequirement.SIGN_IN;
  } else if (TERMS_NOT_REQUIRED_PROVIDERS.includes(providerId)) {
    return ComplianceRequirement.NONE;
  } else if (BROWSER_BASED_TERMS.includes(providerId)) {
    const accepted = isProviderTermsAccepted(providerId, userId);
    return accepted ? ComplianceRequirement.NONE : ComplianceRequirement.TERMS_OF_USE;
  } else if (VOW_PROVIDERS.includes(providerId)) {
    const accepted = isProviderTermsAccepted(providerId, userId);
    return accepted ? ComplianceRequirement.NONE : ComplianceRequirement.TERMS_OF_USE;
  }
  return ComplianceRequirement.NONE;
}

const SCRAMBLE_PROVIDERS = [
  Provider.PROVIDER_PILLAR9_VOW,
  Provider.PROVIDER_REBGV_VOW,
];

function shouldScrambleForProvider(providerId: number, complianceRequirement: ComplianceRequirement): boolean {
  return SCRAMBLE_PROVIDERS.includes(providerId) && complianceRequirement !== ComplianceRequirement.NONE;
}

function isListingVisibleToUser( isPrivate: boolean, providerId: number, acceptedBrowserBasedTerms: Provider[], acceptedTerms: Provider[]): boolean {
  if (isPrivate) return false;
  if (TERMS_NOT_REQUIRED_PROVIDERS.includes(providerId)) {
    return true;
  }
  if (BROWSER_BASED_TERMS.includes(providerId)) {
    return acceptedBrowserBasedTerms.includes(providerId);
  }
  if (VOW_PROVIDERS.includes(providerId)) {
    return acceptedTerms.includes(providerId);
  }
  // If none of the above conditions are met, the listing is visible by default.
  return true;
}


// function isListingVisibleToUserFromRequest(request: IncomingMessage, providerId: number): boolean {
// //   const isAuthenticated = isUserAuthenticatedFromRequest(request);
// //   const isPrivate = isProviderPrivate(providerId) && !isAuthenticated;
// //   if (isPrivate) {
// //     return false;
// //   }
// //   if (TERMS_NOT_REQUIRED_PROVIDERS.includes(providerId)) {
// //     return true;
// //   }
// //   const acceptedTerms = getAcceptedTermsByUserFromRequest(request, userId);
// //   return isListingVisibleToUser(isPrivate, providerId, acceptedTerms.browserBasedTerms, acceptedTerms.userBasedTerms);
// // }

const setZoocasaTermsAreReadInCookies = () => {
  Cookies.set(ZOOCASA_TERMS_READ_COOKIE, 'true', { expires: ACCEPTED_TERMS_EXPIRE_DAYS });
};

/**
 * Retrieves the state of the Zoocasa terms read cookie from an incoming HTTP request.
 *
 * This function checks the Zoocasa terms read cookie in the request headers to determine
 * if the user has read the terms of use. It's designed to work in conjunction with the
 * client-side cookie management system to track user acceptance of terms.
 *
 * @param request The incoming HTTP request object containing cookies
 * @returns `true` if the Zoocasa terms have been read, `false` otherwise
 *
 * @example
 * ```typescript
 * // In getServerSideProps or API route
 * export const getServerSideProps = async (context) => {
 *   const isZoocasaTermsRead = getZoocasaTermsAreReadFromRequest(context.req);
 *
 *   return {
 *     props: {
 *       isZoocasaTermsRead
 *     }
 *   };
 * };
 * ```
 *
 * @remarks
 * - This function is server-side only and should not be called from client-side code
 * - Returns `false` if the cookie doesn't exist or can't be parsed
 */
function getZoocasaTermsAreReadFromRequest(request: IncomingMessage): boolean {
  return 'true' === getStringFromRequestCookie(ZOOCASA_TERMS_READ_COOKIE, request);
}

const checkZoocasaTermsAreReadInCookies = () => {
  return 'true' === Cookies.get(ZOOCASA_TERMS_READ_COOKIE);
};

/**
 * Finds the highest compliance requirement from items based on priority order.
 *
 * Priority order: SIGN_IN > TERMS_OF_USE > NONE
 *
 * @param items Array of items with compliance requirements
 * @returns The highest compliance requirement found across items
 *
 * @example
 * ```typescript
 * const saleHistory = [
 *   { providerId: Provider.PROVIDER_TREB_VOW, complianceRequirement: ComplianceRequirement.TERMS_OF_USE },
 *   { providerId: Provider.PROVIDER_AIKEN_MLS, complianceRequirement: ComplianceRequirement.NONE }
 * ];
 * const rentalHistory = [
 *   { providerId: Provider.PROVIDER_TREB_VOW, complianceRequirement: ComplianceRequirement.SIGN_IN }
 * ];
 *
 * const highestRequirement = getHighestComplianceRequirement([...saleHistory, ...rentalHistory]);
 * // Returns { requirement: ComplianceRequirement.SIGN_IN, providerId: Provider.PROVIDER_TREB_VOW }
 * ```
 */
function getHighestComplianceRequirement( items: ReadonlyArray<{ providerId: number; complianceRequirement?: ComplianceRequirement }> = []
): { requirement: ComplianceRequirement; providerId: Provider } {
  // Priority order: SIGN_IN > TERMS_OF_USE > NONE
  const priorityOrder = {
    [ComplianceRequirement.SIGN_IN]: 3,
    [ComplianceRequirement.TERMS_OF_USE]: 2,
    [ComplianceRequirement.NONE]: 1,
  };

  let highestRequirement = ComplianceRequirement.NONE;
  let highestProviderId = Provider.PROVIDER_UNSPECIFIED;
  let highestPriority = 1;

  for (const item of items) {
    const requirement = item.complianceRequirement || ComplianceRequirement.NONE;
    const priority = priorityOrder[requirement];

    if (priority > highestPriority) {
      highestPriority = priority;
      highestProviderId = item.providerId;
      highestRequirement = requirement;
    }
  }

  return { requirement: highestRequirement, providerId: highestProviderId };
}

export {
  acceptProviderTermsForUser,
  getAcceptedTermsByUserFromCookies,
  isProviderTermsAccepted,
  isProviderTermsAcceptedFromRequest,
  setZoocasaTermsAreReadInCookies,
  checkZoocasaTermsAreReadInCookies,
  getAcceptedTermsByUserFromRequest,
  getZoocasaTermsAreReadFromRequest,
  getAcceptedTermsFromRequest,
  getComplianceRequirementFromRequest,
  getComplianceRequirementFromCookies,
  getHighestComplianceRequirement,
  getProviderLogoUrl,
  isProviderPrivate,
  isVOWProvider,
  isListingVisibleToUser,
  shouldScrambleForProvider,
};
